import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Inject,
  Injector,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { OrderformFascade } from '../../../../fascades/orderform.fascade';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map } from 'rxjs/operators';
import { BehaviorSubject, firstValueFrom, Observable, of, catchError } from 'rxjs';
import { AbstractMethodComponent } from '../abstract-method.component';
import {
  CustomFormData,
  InterfacePaymentProviderStipe,
  OrderFormValidationResult,
  OrderformOrderState,
  OrderformSettingsState,
  PAYMENT_ERROR_MESSAGES,
} from '@orderform/widgets';
import { CREDIT_CARD_DEFAULT_STYLES, CreditCardStyles } from '../../payment-methods.model';
import { OrderformService } from '../../../../services/orderform.service';
import { ExternalResourcesLoaderService } from '../../../../services/external-resources-loader.service';
import { CustomErrorMessageService } from '../../services/custom-error-messages.service';

interface StripeElement {
  addEventListener: (
    type: string,
    listener: (event: { complete?: boolean; error?: { message: string } }) => void
  ) => void;

  mount(selector: string): void;
}

interface StripeElementsCreator {
  create(type: string, options: any): StripeElement;
}

interface StripeInstance {
  elements(): StripeElementsCreator;

  confirmCardSetup(
    secret: string,
    data: any,
    config: any
  ): Promise<{
    error: { message: string; code: string; decline_code: string };
    setupIntent: { id: { card: any } };
  }>;
}

interface StripeResultConfirmCardSetup {
  error: {
    message: string;
    code: string;
    decline_code: string;
  };
  setupIntent: {
    id: {
      card: any;
    };
  };
}
interface StripeAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
}

@Component({
  selector: 'orderform-payment-method-stripe',
  templateUrl: './stripe.component.html',
  styleUrls: ['./stripe.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StripeComponent),
      multi: true,
    },
  ],
})
export class StripeComponent
  extends AbstractMethodComponent
  implements OnInit, OnDestroy, ControlValueAccessor, AfterViewInit
{
  @Input() config: InterfacePaymentProviderStipe;

  @Input() styles?: CreditCardStyles = CREDIT_CARD_DEFAULT_STYLES;

  public stripeError$ = {
    number: new BehaviorSubject<string>(''),
    expire: new BehaviorSubject<string>(''),
    cvc: new BehaviorSubject<string>(''),
  };
  private _stripeComplete = {
    number: false,
    expire: false,
    cvc: false,
  };
  private readonly _subscriptionBeforeSubmit = this._orderformFascade.beforeSubmit.subscribe(() =>
    this._onBeforeSubmit()
  );
  private readonly _subscriptionFormValidation = this._orderformFascade.formValidation.subscribe(
    () => this._onFormValidation(),
    this._element
  );

  private _settings: OrderformSettingsState;
  private readonly _settings$ = this._orderformFascade.settings$.subscribe((settings) => {
    this._settings = settings;
  });

  private _stripeInstance: StripeInstance;
  private _stripeCardNumber: StripeElement;
  private _stripeCardExpire: StripeElement;
  private _stripeCardCvc: StripeElement;
  private _stripeToken: { card: any };
  private _waitStripeInitialized: Promise<boolean>;

  constructor(
    private readonly _orderformFascade: OrderformFascade,
    private readonly _orderFormService: OrderformService,
    @Inject(LOCALE_ID) private readonly _locale: string,
    injector: Injector,
    private customErrorMessageService: CustomErrorMessageService,
    public externalResourcesLoader: ExternalResourcesLoaderService
  ) {
    super(injector);
  }

  getStripe(): (publicKey: string, config: { locale: string }) => StripeInstance {
    return (<any>window).Stripe;
  }

  ngOnInit(): void {
    this._waitStripeInitialized = (async () => {
      if (!this.getStripe()) {
        await this.externalResourcesLoader.load(this._document, [this.config.apiUrl], [], true);
      }

      /**
       * get Stripe Function from asset load (_loadExternalResources)
       */
      const stripeFunction = this.getStripe();

      /**
       * Check stripe has loaded right
       */
      if (!stripeFunction) {
        console.log('Stripe not exists');
        return false;
      }

      /**
       * create instance
       */
      this._stripeInstance = stripeFunction(this.config.publicKey, {
        locale: this._locale.replace(/-[a-zA-Z0-9]{2,3}$/, ''),
      });

      const elements = this._stripeInstance.elements();
      const style = {
        base: {
          iconColor: this.styles.textColor,
          color: this.styles.textColor,
          fontSize: parseFloat(this.styles.fontSize) * 16 + 'px', // send as px; 1rem = 16px
          ':-webkit-autofill': {
            color: this.styles.placeholderColor,
          },
          '::placeholder': {
            color: this.styles.placeholderColor,
          },
        },
        invalid: {
          iconColor: this.styles.textColor,
          color: this.styles.textColor,
        },
      };

      const classes = {
        complete: 'ng-valid',
        invalid: 'ng-invalid',
      };

      this._stripeCardNumber = elements.create('cardNumber', {
        placeholder: '4444 3333 2222 1111',
        showIcon: true,
        style,
        classes,
      });

      this._stripeCardExpire = elements.create('cardExpiry', {
        placeholder: '03 / ' + ('' + (new Date().getFullYear() + 2)).slice(-2),
        style,
        classes,
      });

      this._stripeCardCvc = elements.create('cardCvc', {
        placeholder: '●●●',
        style: {
          ...style,
          base: {
            ...style.base,
            ':-webkit-autofill': {
              color: '#E6E9EC',
            },
            '::placeholder': {
              color: '#E6E9EC',
            },
          },
        },
        classes,
      });

      const fields = {
        number: this._stripeCardNumber,
        expire: this._stripeCardExpire,
        cvc: this._stripeCardCvc,
      };
      Object.keys(fields).forEach((field) => {
        fields[field].addEventListener('change', (event) => {
          this._stripeComplete[field] = event.complete;
          this._paymentDataChanged.next({ valid: this._isValid() });
          if (event.error) {
            this.stripeError$[field].next(event.error.message);
          } else {
            this.stripeError$[field].next('');
          }
        });
      });

      return true;
    })();
  }

  public async ngAfterViewInit() {
    if ((await this._waitStripeInitialized) === false) {
      return;
    }
    this._stripeCardNumber.mount('#stripe-card-number-' + this.uniqueInstanceId);
    this._stripeCardExpire.mount('#stripe-card-expire-' + this.uniqueInstanceId);
    this._stripeCardCvc.mount('#stripe-card-cvc-' + this.uniqueInstanceId);
  }

  private _onBeforeSubmit(): Promise<{ valid: boolean }> {
    if (!this.isActive()) {
      return Promise.resolve({ valid: true });
    }
    if (this._stripeToken) {
      this._paymentDataChanged.next({
        valid: true,
        stripe_token: this._stripeToken,
      });
      return Promise.resolve({
        valid: true,
      });
    } else {
      this._paymentDataChanged.next({
        valid: false,
      });
    }

    if (!this._isValid()) {
      return Promise.resolve({ valid: false });
    }
    // Get Stripe Token
    return firstValueFrom(this._orderformFascade.order$).then((orderData) => {
      const payment_method: { card: any; billing_details?: any } = {
        card: this._stripeCardNumber,
      };

      payment_method.billing_details = {
        address: this._mapToStripeAddress(
          orderData.order.has_billing_address === 0 ? orderData.order.shipping_address : orderData.order.billing_address
        ),
      };

      return this._stripeInstance
        .confirmCardSetup(
          this.config.intentClientSecret,
          { payment_method },
          {
            handleActions: false,
          }
        )
        .then((result) => {
          if (result.error) {
            const declinedErrorMessage = this.customErrorMessageService.getCustomErrorMessageFromOrderData(
              result,
              orderData
            );
            this._toaster.error(declinedErrorMessage);

            this._monitoring.trackValidationError(
              'exception:' + result.error.code + ':' + JSON.stringify(result.error),
              'frontend',
              'ds-orderform-payment-stripe'
            );

            const labels = this._monitoring.getLabelsFromFailedTransaction(
              orderData,
              result.error,
              'ds-orderform-payment-stripe'
            );
            this._monitoring.addLabels(labels);

            return { valid: false };
          } else {
            this._stripeToken = result.setupIntent.id;
            this._paymentDataChanged.next({
              valid: true,
              stripe_token: result.setupIntent.id,
            });
            return { valid: true };
          }
        });
    });
  }

  private _mapToStripeAddress(addressInput: CustomFormData): StripeAddress {
    const stripeAddress: StripeAddress = {};
    if (!addressInput) {
      return stripeAddress;
    }

    const StripeAddressMapping: Partial<{
      [P in keyof StripeAddress]: string[];
    }> = {
      line1: ['buyer_address_street', 'buyer_address_street_billing'],
      line2: ['buyer_address_street2', 'buyer_address_street2_billing'],
      city: ['buyer_address_city', 'buyer_address_city_billing'],
      state: ['buyer_address_state', 'buyer_address_state_billing'],
      country: [
        'buyer_address_private_country',
        'buyer_address_private_country_billing',
        'country',
        'country_billing',
        'address_country',
        'address_country_billing',
        'buyer_address_country',
        'buyer_address_country_billing',
      ],
      postal_code: [
        'buyer_address_private_zipcode',
        'buyer_address_private_zipcode_billing',
        'buyer_address_zipcode',
        'buyer_address_zipcode_billing',
      ],
    };

    Object.keys(StripeAddressMapping).forEach((stripeKey) => {
      const addressInputKeys = StripeAddressMapping[stripeKey];
      const foundAddressInputKey = addressInputKeys.find((addressInputKey) =>
        addressInput.hasOwnProperty(addressInputKey)
      );
      if (foundAddressInputKey && addressInput[foundAddressInputKey]) {
        stripeAddress[stripeKey] = addressInput[foundAddressInputKey];
      }
    });

    return stripeAddress;
  }

  private _isValid() {
    const notCompletedFields = Object.keys(this._stripeComplete).filter((field) => !this._stripeComplete[field]);
    const notCompleted = notCompletedFields.length > 0;
    return !notCompleted;
  }

  ngOnDestroy() {
    this._subscriptionBeforeSubmit.unsubscribe();
    this._subscriptionFormValidation.unsubscribe();
    this._settings$.unsubscribe();
  }

  private _onFormValidation(): Promise<OrderFormValidationResult> {
    if (!this.isActive() || this._isValid()) {
      return Promise.resolve({
        valid: true,
        component: 'ds-orderform-payment-stripe',
        invalidFieldList: [],
      });
    }
    const notCompletedFields = Object.keys(this._stripeComplete).filter((field) => !this._stripeComplete[field]);
    // show generic error
    const invalidFieldList: { id: string; errors: string[] }[] = [];
    notCompletedFields.forEach((field) => {
      if (this.stripeError$[field].getValue() === '') {
        this.stripeError$[field].next(
          $localize`:@@payment_method.label.stripe_incomplete_generic:Die Eingabe wird benötigt.`
        );
        invalidFieldList.push({ id: field, errors: ['required'] });
      } else {
        invalidFieldList.push({
          id: field,
          errors: [this.stripeError$[field].getValue()],
        });
      }
    });
    return Promise.resolve({
      valid: false,
      component: 'ds-orderform-payment-stripe',
      invalidFieldList,
    });
  }
}
